import { supabase } from "../supabase/client";
import { getCurrentEmpresaId } from "../supabase/auth-utils";

/**
 * 📅 Função auxiliar: Verifica se deve usar campos da Reforma Tributária (IBS/CBS)
 * Reforma vigora a partir de 01/04/2026
 */
export function deveUsarCamposReforma(): boolean {
  const dataReforma = new Date('2026-04-01T00:00:00Z');
  const agora = new Date();
  const resultado = agora >= dataReforma;
  
  if (resultado) {
    console.log('📅 ✅ REFORMA TRIBUTÁRIA ATIVA - Usando campos IBS/CBS');
  } else {
    const diasRestantes = Math.ceil((dataReforma.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24));
    console.log(`📅 ⏳ MODO CLÁSSICO - Reforma em ${diasRestantes} dias`);
  }
  
  return resultado;
}

export interface FocusNFConfig {
  id?: string;
  empresa_id: string;
  focus_empresa_id?: string;
  token_producao?: string;
  token_homologacao?: string;
  ambiente: "homologacao" | "producao";
  // ✅ Habilitação de documentos fiscais (para Focus)
  habilita_nfe?: boolean;      // NFe (Modelo 55) - padrão true
  habilita_nfce?: boolean;     // NFCe (Modelo 65) - consumidor
  habilita_nfse?: boolean;     // NFS-e - serviços
  nfe_serie?: string;
  nfe_proximo_numero?: number;
  // ✅ NOVO - Campos NFC-e (Modelo 65)
  nfce_habilitada?: boolean;   // Flag interna para PDV (manter por compatibilidade)
  nfce_csc?: string;           // CSC do SEFAZ-MG para NFC-e
  nfce_id_token?: string;      // ID Token do SEFAZ-MG para NFC-e
  nfce_serie?: string;         // Série específica para NFC-e
  nfce_proximo_numero?: number; // Próximo número para NFC-e
  // ✅ NOVO - Campos NFS-e
  nfse_serie_homologacao?: string;
  nfse_proximo_rps_homologacao?: number;
  nfse_serie_producao?: string;
  nfse_proximo_rps_producao?: number;
  nfse_login_prefeitura?: string;
  nfse_senha_prefeitura?: string;
  // ✅ NOVO - Campos NFS-e Nacional (NFSEN)
  habilita_nfsen?: boolean;
  nfsen_homologacao_habilitada?: boolean;
  nfsen_producao_habilitada?: boolean;
  cnpj_emitente: string;
  razao_social: string;
  nome_fantasia?: string;
  inscricao_estadual?: string;
  inscricao_municipal?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
  telefone?: string;
  email?: string;
  certificado_arquivo?: string; // Base64 do certificado A1 (.pfx/.p12)
  certificado_senha?: string; // Senha do certificado
  regime_tributario?:
    | "simples_nacional"
    | "simples_excesso_sublimite"
    | "regime_normal"
    | "mei";
  ativo?: boolean;
}

export interface FocusNFNota {
  id?: string;
  empresa_id: string;
  order_id?: string;
  order_code?: string;
  ref: string;
  tipo_nota: "NFe" | "NFCe" | "NFSe" | "CTe" | "MDFe" | "NFCom" | "MDe";
  status: string;
  numero?: string;
  serie?: string;
  chave_acesso?: string;
  valor_total?: number;
  xml_url?: string;
  danfe_url?: string;
  ambiente?: string;
  erro_mensagem?: string;
  dados_enviados?: Record<string, unknown>;
  dados_retornados?: Record<string, unknown>;
}

export interface NotaItem {
  // Campos básicos
  numero_item: number;
  codigo_produto: string;
  descricao: string;
  ncm: string;
  cfop: string;
  unidade: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;

  // 📅 REFORMA TRIBUTÁRIA 2026 (01/04/2026)
  // Os campos abaixo são OPCIONAIS e tratados automaticamente pelo backend:
  // - ANTES de 01/04: Sistema usa ICMS/PIS/COFINS (campos clássicos)
  // - A PARTIR de 01/04: Sistema usa IBS/CBS/IPI (campos novos)
  // - Backend decide automaticamente baseado na data do servidor
  // - Se você fornecer esses campos, serão utilizados; caso contrário, calculados
  
  // NOVOS CAMPOS - REFORMA TRIBUTÁRIA (IBS/CBS)
  // Campos opcionais - calculados automaticamente se não fornecidos
  ibs_cbs_situacao_tributaria?: string; // "00" = Normal
  ibs_cbs_classificacao_tributaria?: string; // Código de classificação (7 dígitos)
  ibs_cbs_base_calculo?: number; // Base de cálculo
  
  // CBS (Contribuição sobre Bens e Serviços)
  cbs_aliquota?: number; // 0.009 = 0.9% em 2026
  cbs_valor?: number; // Valor calculado

  // IBS (Imposto sobre Bens e Serviços)
  ibs_uf_aliquota?: number; // 0.001 = 0.1% estadual em 2026
  ibs_uf_valor?: number; // Valor calculado
  ibs_mun_aliquota?: number; // 0 = 0% municipal em 2026
  ibs_mun_valor?: number; // Valor calculado
  ibs_valor_total?: number; // Total IBS (UF + MUN)

  // IPI (Imposto sobre Produtos Industrializados) - Novo na Reforma
  ipi_situacao_tributaria?: string; // "01" = Tributado; "02" = Isento
  ipi_aliquota?: number; // Alíquota do IPI
  ipi_valor?: number; // Valor calculado
}

export interface EmitirNotaParams {
  orderCode: string;
  tipoNota?: "NFe" | "NFCe" | "NFSe";
  cliente?: {
    nome: string;
    cpf_cnpj?: string;
    email?: string;
    telefone?: string;
    endereco?: {
      logradouro: string;
      numero: string;
      complemento?: string;
      bairro: string;
      cidade: string;
      uf: string;
      cep: string;
    };
  };
  valorTotal?: number;
  items?: NotaItem[]; // Múltiplos itens (opcional)
  nfse?: {
    prestadorCodigoMunicipio: string;
    prestadorInscricaoMunicipal?: string;
    tomadorCodigoMunicipio: string;
    codigoMunicipioPrestacao?: string;
    itemListaServico: string;
    codigoTributarioMunicipio?: string;
    aliquota: number;
    issRetido: boolean;
    discriminacao: string;
    naturezaOperacao?: number;
    regimeEspecialTributacao?: number;
    optanteSimplesNacional?: boolean;
    incentivadorCultural?: boolean;
    // NT007 - NFS-e Nacional: apuração e retenção PIS/COFINS/CSLL/IRRF
    baseCalculoPisCofins?: number | string;
    aliquotaPis?: number | string;
    aliquotaCofins?: number | string;
    valorPis?: number | string;
    valorCofins?: number | string;
    tipoRetencaoPisCofins?: number | string;
    valorCsll?: number | string;
    valorIrrf?: number | string;
    // Campos oficiais flat da API NFSEN
    codigoOpcaoSimplesNacional?: number | string;
    tributacaoIss?: number | string;
  };
}

class FocusNFService {
  private async makeRequest(
    action: string,
    data: unknown,
    options: { skipHomologacaoCheck?: boolean } = {}
  ) {
    console.log(`🔄 Focus NF Request: ${action}`, data);

    if (!options.skipHomologacaoCheck && action !== "cadastrarEmitente") {
      const configAtual = await this.getConfig();
      if (
        configAtual?.ambiente === "homologacao" &&
        !configAtual.token_homologacao?.trim()
      ) {
        throw new Error(
          "Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação."
        );
      }
    }

    // Obter token de autenticação do Supabase
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const authToken = session?.access_token;

    const response = await fetch("/api/focusnf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify({ action, data: { ...(data as any), authToken } }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error(`❌ Focus NF API Error: ${response.status}`, responseData);
      console.error(`📋 Resposta completa da API:`, JSON.stringify(responseData, null, 2));
      
      const mensagemSefaz =
        responseData?.mensagem_sefaz ||
        responseData?.error ||
        responseData?.mensagem ||
        responseData?.details ||
        JSON.stringify(responseData) ||
        "Erro desconhecido";
      
      console.error(`🔴 Mensagem de erro: ${mensagemSefaz}`);
      throw new Error(mensagemSefaz);
    }

    console.log(`✅ Focus NF Response: ${action}`, responseData);
    return responseData;
  }

  async saveConfig(
    config: FocusNFConfig
  ): Promise<{ ok: boolean; data?: FocusNFConfig; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const { data: existing } = await supabase
        .from("focusnf_config")
        .select("id")
        .eq("empresa_id", empresa_id)
        .maybeSingle();

      // Formatar dados antes de salvar (remover pontuação)
      const nfeProximoNumero =
        typeof config.nfe_proximo_numero === "number"
          ? Math.max(1, Math.floor(config.nfe_proximo_numero))
          : config.nfe_proximo_numero
          ? Math.max(1, parseInt(String(config.nfe_proximo_numero), 10))
          : undefined;

      const configData = {
        ...config,
        cnpj_emitente:
          config.cnpj_emitente?.replace(/\D/g, "") || config.cnpj_emitente,
        endereco_cep:
          config.endereco_cep?.replace(/\D/g, "") || config.endereco_cep,
        telefone: config.telefone?.replace(/\D/g, "") || config.telefone,
        endereco_uf:
          config.endereco_uf
            ?.toUpperCase()
            .replace(/[^A-Z]/g, "")
            .substring(0, 2) || config.endereco_uf,
        nfe_serie: config.nfe_serie?.trim() || config.nfe_serie,
        nfe_proximo_numero: nfeProximoNumero,
        empresa_id,
        ativo: true,
        updated_at: new Date().toISOString(),
      };

      let result;
      if (existing) {
        const { data, error } = await supabase
          .from("focusnf_config")
          .update(configData)
          .eq("id", existing.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from("focusnf_config")
          .insert(configData)
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      return { ok: true, data: result as FocusNFConfig };
    } catch (e: any) {
      console.error("Erro ao salvar configuração Focus NF:", e);
      const rawMessage = String(e?.message || "");
      const missingColumnMatch = rawMessage.match(/Could not find the '([^']+)' column/i);
      const isSchemaCacheError = /schema cache/i.test(rawMessage);

      if (missingColumnMatch || isSchemaCacheError) {
        const coluna = missingColumnMatch?.[1];
        const detalheColuna = coluna ? ` (coluna: ${coluna})` : "";
        return {
          ok: false,
          error:
            `Seu banco de dados está desatualizado para esta funcionalidade${detalheColuna}. ` +
            "Aplique as migrations mais recentes do Supabase e tente salvar novamente.",
        };
      }

      return { ok: false, error: e.message || "Erro ao salvar configuração" };
    }
  }

  async getConfig(): Promise<FocusNFConfig | null> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) return null;

      const { data, error } = await supabase
        .from("focusnf_config")
        .select("*")
        .eq("empresa_id", empresa_id)
        .eq("ativo", true)
        .maybeSingle();

      if (error) throw error;
      return data as FocusNFConfig | null;
    } catch (e) {
      console.error("Erro ao buscar configuração:", e);
      return null;
    }
  }

  async atualizarEmitenteNaFocus(): Promise<{
    ok: boolean;
    data?: { id?: string; cnpj: string; status?: string };
    error?: string;
  }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const result = await this.makeRequest("atualizarEmitente", {
        empresaId: empresa_id,
      });

      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao atualizar emitente" };
    }
  }

  /**
   * Emissão idempotente:
   * - ref é gerada e persistida ANTES do POST na Focus
   * - ref é IMUTÁVEL: uma vez criada para (empresa_id, order_code), não muda mais
   * - não reemite se estiver processando: orienta a consultar
   */
  async emitirNota(
    params: EmitirNotaParams
  ): Promise<{ ok: boolean; data?: FocusNFNota; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const configAtual = await this.getConfig();
      if (!configAtual) {
        return {
          ok: false,
          error:
            "Configuração Focus NF não encontrada. Configure na página de configurações.",
        };
      }

      if (
        configAtual.ambiente === "homologacao" &&
        !configAtual.token_homologacao?.trim()
      ) {
        return {
          ok: false,
          error:
            "Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.",
        };
      }

      // Regras de status:
      // - Final: nunca reemitir (ref não pode ser reutilizada em outra tentativa "nova")
      // - Processando: não reemitir; deve consultar (poll)
      // - Erro: pode reemitir com a MESMA ref
      const statusFinal = new Set(["autorizado", "cancelado"]);
      const statusProcessando = new Set(["processando_autorizacao"]);
      const statusErro = new Set([
        "erro_autorizacao",
        "erro_validacao",
        "erro_emissao",
      ]);

      // Pega a nota mais recente por order_code
      const { data: notaExistente, error: notaExistenteError } = await supabase
        .from("focusnf_notas")
        .select("*")
        .eq("empresa_id", empresa_id)
        .eq("order_code", params.orderCode)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (notaExistenteError) {
        console.error("Erro ao buscar nota existente:", notaExistenteError);
      }

      if (notaExistente) {
        if (statusFinal.has(notaExistente.status)) {
          return {
            ok: false,
            error: "Já existe uma nota finalizada (autorizada/cancelada) para este pedido.",
          };
        }

        if (statusProcessando.has(notaExistente.status)) {
          return {
            ok: false,
            error:
              "Já existe uma nota em processamento para este pedido. Aguarde e consulte o status em vez de reemitir.",
          };
        }

        if (statusErro.has(notaExistente.status)) {
          return {
            ok: false,
            error:
              "Existe uma nota com erro para este pedido. Use a ação de reemitir para gerar uma nova referência.",
          };
        }
      }

      const tipoNota = params.tipoNota || "NFe";
      let serieReservada: string | undefined;
      let numeroReservado: number | undefined;

      if (tipoNota === "NFe") {
        const reservaNumero = await this.reserveNfeNumero(empresa_id);
        if (!reservaNumero.ok) {
          return {
            ok: false,
            error:
              reservaNumero.error ||
              "Não foi possível reservar o próximo número da NF-e.",
          };
        }

        serieReservada = reservaNumero.data?.serie || "1";
        numeroReservado = reservaNumero.data?.numero || 1;
      }

      // 1) Garantir ref imutável (criar registro antes do POST)
      // Estratégia mais robusta: tentar INSERT para sempre criar uma nova linha (histórico).
      const ref = `${crypto.randomUUID()}-${Date.now()}`;
      
      // Converter numero para string e garantir que não seja undefined/null
      const numeroStr = numeroReservado ? String(numeroReservado) : "1";
      const serieStr = serieReservada || "1";
      
      // Validar campos obrigatórios
      if (!empresa_id) throw new Error("empresa_id é obrigatório");
      if (!params.orderCode) throw new Error("orderCode é obrigatório");
      if (!ref) throw new Error("ref é obrigatório");
      
      // Armazenar apenas dados essenciais para evitar erro 22001 (string truncation)
      const dadosEnviados = {
        orderCode: params.orderCode,
        tipoNota,
        valorTotal: params.valorTotal || 0,
        clienteCPFCNPJ: params.cliente?.cpf_cnpj,
        clienteNome: params.cliente?.nome,
        itemsCount: params.items?.length || 0,
        nfse: params.nfse
          ? {
              prestadorCodigoMunicipio: params.nfse.prestadorCodigoMunicipio,
              prestadorInscricaoMunicipal: params.nfse.prestadorInscricaoMunicipal,
              tomadorCodigoMunicipio: params.nfse.tomadorCodigoMunicipio,
              codigoMunicipioPrestacao: params.nfse.codigoMunicipioPrestacao,
              itemListaServico: params.nfse.itemListaServico,
              codigoTributarioMunicipio: params.nfse.codigoTributarioMunicipio,
              aliquota: params.nfse.aliquota,
              issRetido: params.nfse.issRetido,
              discriminacao: params.nfse.discriminacao,
              naturezaOperacao: params.nfse.naturezaOperacao,
              regimeEspecialTributacao: params.nfse.regimeEspecialTributacao,
              optanteSimplesNacional: params.nfse.optanteSimplesNacional,
              incentivadorCultural: params.nfse.incentivadorCultural,
            }
          : undefined,
        serie: serieStr,
        numero: numeroStr,
        ref,
      };

      // Tenta inserir (falha se houver constraint única em empresa_id+order_code)
      let notaRow: any | null = null;

      console.log("🔧 [focusNFService] Inserindo nota com:", {
        empresa_id,
        order_code: params.orderCode,
        ref,
        tipo_nota: tipoNota,
        status: "processando_autorizacao",
        valor_total: params.valorTotal || 0,
        ambiente: configAtual.ambiente,
        numero: numeroStr,
        serie: serieStr,
        dados_enviados: JSON.stringify(dadosEnviados).substring(0, 100) + "...",
      });

      const inserData = {
        empresa_id,
        order_code: params.orderCode,
        ref,
        tipo_nota: tipoNota,
        status: "processando_autorizacao",
        valor_total: params.valorTotal || 0,
        ambiente: configAtual.ambiente,
        numero: numeroStr,
        serie: serieStr,
        dados_enviados: dadosEnviados,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log("📤 Payload completo:", JSON.stringify(inserData, null, 2));

      const { data: inserted, error: insertError } = await supabase
        .from("focusnf_notas")
        .insert(inserData)
        .select("*")
        .maybeSingle();

      if (!insertError && inserted) {
        notaRow = inserted;
      } else {
        console.error("❌ Falha ao inserir nota:", insertError);
        console.error("📋 Erro completo:", JSON.stringify(insertError, null, 2));
        console.error("📋 Payload que foi enviado:", {
          empresa_id,
          order_code: params.orderCode,
          ref,
          tipo_nota: tipoNota,
          numero: numeroStr,
          serie: serieStr,
          status: "processando_autorizacao",
          valor_total: params.valorTotal || 0,
          ambiente: configAtual.ambiente,
        });
        return {
          ok: false,
          error:
            "Não foi possível criar o registro da nota. Verifique se a migração de reemissão foi aplicada.",
        };
      }

      // 2) Emitir usando a ref persistida (imutável)
      return await this.emitirNotaComRef(
        notaRow.ref,
        params,
        notaRow.id,
        empresa_id,
        {
          serie: serieReservada,
          numero: numeroReservado,
          dadosEnviados,
        }
      );
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao emitir nota" };
    }
  }

  async reemitirNota(
    orderCode: string,
    params: EmitirNotaParams,
    motivo?: string
  ): Promise<{ ok: boolean; data?: FocusNFNota; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const configAtual = await this.getConfig();
      if (
        configAtual?.ambiente === "homologacao" &&
        !configAtual.token_homologacao?.trim()
      ) {
        return {
          ok: false,
          error:
            "Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.",
        };
      }

      const { data: notaAnterior, error: notaAnteriorError } = await supabase
        .from("focusnf_notas")
        .select("*")
        .eq("empresa_id", empresa_id)
        .eq("order_code", orderCode)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (notaAnteriorError) {
        console.error("Erro ao buscar nota anterior:", notaAnteriorError);
      }

      const dadosAnteriores = notaAnterior?.dados_enviados as
        | Record<string, unknown>
        | undefined;

      if (!dadosAnteriores && !params?.cliente && !params?.items) {
        return {
          ok: false,
          error:
            "Nota anterior não possui payload salvo. Reemita pelo pedido para informar os dados completos.",
        };
      }

      const payloadBase = dadosAnteriores || {};
      const payloadParams = {
        orderCode,
        tipoNota: params.tipoNota || (payloadBase as any).tipoNota,
        cliente: params.cliente || (payloadBase as any).cliente,
        valorTotal:
          params.valorTotal ?? (payloadBase as any).valorTotal ?? undefined,
        items: params.items || (payloadBase as any).items,
        nfse: params.nfse || (payloadBase as any).nfse,
      } as EmitirNotaParams;

      const tipoNota = payloadParams.tipoNota || "NFe";

      if (tipoNota === "NFSe") {
        const nfse = payloadParams.nfse;
        if (!nfse?.prestadorCodigoMunicipio?.trim()) {
          return {
            ok: false,
            error:
              "NFS-e sem código do município do prestador. Reabra o modal de emissão do pedido, preencha os dados fiscais da NFS-e e tente novamente.",
          };
        }
        if (!nfse?.tomadorCodigoMunicipio?.trim()) {
          return {
            ok: false,
            error:
              "NFS-e sem código do município do tomador. Reabra o modal de emissão do pedido e preencha os dados fiscais.",
          };
        }
        if (!nfse?.itemListaServico?.trim()) {
          return {
            ok: false,
            error:
              "NFS-e sem item da lista de serviço. Reabra o modal de emissão do pedido e preencha os dados fiscais.",
          };
        }
      }

      let serieReservada: string | undefined;
      let numeroReservado: number | undefined;

      if (tipoNota === "NFe") {
        const reservaNumero = await this.reserveNfeNumero(empresa_id);
        if (!reservaNumero.ok) {
          return {
            ok: false,
            error:
              reservaNumero.error ||
              "Não foi possível reservar o próximo número da NF-e.",
          };
        }

        serieReservada = reservaNumero.data?.serie || "1";
        numeroReservado = reservaNumero.data?.numero || 1;
      }
      const ref = `${crypto.randomUUID()}-${Date.now()}`;
      
      // Converter numero para string e garantir que não seja undefined/null
      const numeroStr = numeroReservado ? String(numeroReservado) : "1";
      const serieStr = serieReservada || "1";
      
      // Armazenar apenas dados essenciais para evitar erro 22001 (string truncation)
      const dadosEnviados = {
        orderCode,
        tipoNota: payloadParams.tipoNota || tipoNota,
        valorTotal: payloadParams.valorTotal || 0,
        clienteCPFCNPJ: payloadParams.cliente?.cpf_cnpj,
        clienteNome: payloadParams.cliente?.nome,
        itemsCount: payloadParams.items?.length || 0,
        nfse: payloadParams.nfse
          ? {
              prestadorCodigoMunicipio: payloadParams.nfse.prestadorCodigoMunicipio,
              prestadorInscricaoMunicipal: payloadParams.nfse.prestadorInscricaoMunicipal,
              tomadorCodigoMunicipio: payloadParams.nfse.tomadorCodigoMunicipio,
              codigoMunicipioPrestacao: payloadParams.nfse.codigoMunicipioPrestacao,
              itemListaServico: payloadParams.nfse.itemListaServico,
              codigoTributarioMunicipio: payloadParams.nfse.codigoTributarioMunicipio,
              aliquota: payloadParams.nfse.aliquota,
              issRetido: payloadParams.nfse.issRetido,
              discriminacao: payloadParams.nfse.discriminacao,
              naturezaOperacao: payloadParams.nfse.naturezaOperacao,
              regimeEspecialTributacao: payloadParams.nfse.regimeEspecialTributacao,
              optanteSimplesNacional: payloadParams.nfse.optanteSimplesNacional,
              incentivadorCultural: payloadParams.nfse.incentivadorCultural,
            }
          : undefined,
        serie: serieStr,
        numero: numeroStr,
        ref,
        motivo,
        reemissao: true,
      };

      const { data: inserted, error: insertError } = await supabase
        .from("focusnf_notas")
        .insert({
          empresa_id,
          order_code: orderCode,
          ref,
          tipo_nota: tipoNota,
          status: "processando_autorizacao",
          valor_total: payloadParams.valorTotal || 0,
          ambiente: configAtual?.ambiente || "producao",
          numero: numeroStr,
          serie: serieStr,
          dados_enviados: dadosEnviados,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select("*")
        .maybeSingle();

      if (insertError || !inserted) {
        console.error("❌ Erro ao inserir nota reemitida:", insertError);
        return {
          ok: false,
          error: "Não foi possível criar a nova nota para reemissão.",
        };
      }

      return await this.emitirNotaComRef(
        ref,
        payloadParams,
        inserted.id,
        empresa_id,
        {
          serie: serieReservada,
          numero: numeroReservado,
          dadosEnviados,
        }
      );
    } catch (error: any) {
      return { ok: false, error: error.message || "Erro ao reemitir nota" };
    }
  }

  private async emitirNotaComRef(
    ref: string,
    params: EmitirNotaParams,
    notaId: string | undefined,
    empresa_id: string,
    extra?: {
      serie?: string;
      numero?: number;
      dadosEnviados?: Record<string, unknown>;
    }
  ): Promise<{ ok: boolean; data?: FocusNFNota; error?: string }> {
    try {
      console.log("[FocusNF] emitirNota request", {
        tipoNota: params.tipoNota || "NFe",
        ref,
        empresa_id,
        orderCode: params.orderCode,
      });
      const result = await this.makeRequest("emitirNota", {
        ...params,
        empresaId: empresa_id,
        ref,
        nfeSerie: extra?.serie,
        nfeNumero: extra?.numero,
      }, { skipHomologacaoCheck: true });

      const statusRetorno = result.data?.status;
      const isErroStatus =
        typeof statusRetorno === "string" &&
        (statusRetorno.startsWith("erro") || statusRetorno === "denegado");

      if (result.success && result.data && notaId && !isErroStatus) {
        const { error: updateError } = await supabase
          .from("focusnf_notas")
          .update({
            tipo_nota: params.tipoNota,
            status: result.data.status,
            numero: result.data.numero,
            serie: result.data.serie,
            chave_acesso: result.data.chave_acesso,
            xml_url: result.data.xml_url,
            danfe_url: result.data.danfe_url,
            ambiente: result.data.ambiente,
            dados_retornados: result.data.dados_retornados,
            dados_enviados: extra?.dadosEnviados,
            updated_at: new Date().toISOString(),
          })
          .eq("id", notaId);
        if (updateError) {
          console.error("Erro ao atualizar nota após emissão:", updateError);
          return {
            ok: false,
            error:
              "Nota emitida na Focus, mas não foi possível salvar no sistema. Tente atualizar o status.",
          };
        }
      } else if (notaId) {
        const erroMensagem = this.formatarErroEmissao(
          result.error || result.data?.mensagem || statusRetorno
        );
        const mensagemErro =
          result.error || result.data?.mensagem || statusRetorno || "";
        const dadosRetornados = {
          ...(result.data?.dados_retornados || {}),
          ...(result.data || {}),
          requires_numero_update: this.isErroDuplicidadeNumero(mensagemErro),
        };
        const { error: updateError } = await supabase
          .from("focusnf_notas")
          .update({
            status:
              typeof statusRetorno === "string" ? statusRetorno : "erro_emissao",
            erro_mensagem: erroMensagem,
            dados_retornados: dadosRetornados,
            dados_enviados: extra?.dadosEnviados,
            updated_at: new Date().toISOString(),
          })
          .eq("id", notaId);
        if (updateError) {
          console.error("Erro ao salvar falha da nota:", updateError);
        }
      }

      if (isErroStatus) {
        return {
          ok: false,
          data: result.data,
          error: result.error || result.data?.mensagem || "Erro ao emitir nota",
        };
      }

      return { ok: result.success, data: result.data, error: result.error };
    } catch (error: any) {
      if (notaId) {
        const erroMensagem = this.formatarErroEmissao(error.message);
        const dadosRetornados = {
          requires_numero_update: this.isErroDuplicidadeNumero(error.message),
        };
        const { error: updateError } = await supabase
          .from("focusnf_notas")
          .update({
            status: "erro_emissao",
            erro_mensagem: erroMensagem,
            dados_retornados: dadosRetornados,
            dados_enviados: extra?.dadosEnviados,
            updated_at: new Date().toISOString(),
          })
          .eq("id", notaId);
        if (updateError) {
          console.error("Erro ao salvar falha da nota:", updateError);
        }
      }
      return { ok: false, error: error.message || "Erro ao emitir nota" };
    }
  }

  private async reserveNfeNumero(
    empresa_id: string
  ): Promise<{ ok: boolean; data?: { serie: string; numero: number }; error?: string }> {
    try {
      const { data, error } = await supabase.rpc("reserve_nfe_numero", {
        p_empresa_id: empresa_id,
      });

      if (error || !data || data.length === 0) {
        console.error("Erro ao reservar número NF-e:", error);
        return {
          ok: false,
          error: "Não foi possível reservar a numeração da NF-e.",
        };
      }

      const reserva = Array.isArray(data) ? data[0] : data;
      return {
        ok: true,
        data: {
          serie: reserva.nfe_serie || "1",
          numero: Number(reserva.numero_reservado) || 1,
        },
      };
    } catch (error: any) {
      console.error("Erro ao reservar número NF-e:", error);
      return {
        ok: false,
        error: error.message || "Erro ao reservar número NF-e",
      };
    }
  }

  private isErroDuplicidadeNumero(mensagem?: string) {
    if (!mensagem) return false;
    return /539|duplicidade|chave de acesso/i.test(mensagem);
  }

  private formatarErroEmissao(mensagem?: string) {
    if (!mensagem) {
      return "Erro desconhecido ao emitir nota";
    }

    if (this.isErroDuplicidadeNumero(mensagem)) {
      return "A SEFAZ indica duplicidade de numeração. Ajuste o Próximo Número na Configuração (conforme contabilidade/SEFAZ) e reemita.";
    }

    return mensagem;
  }

  async consultarNota(
    ref: string,
    completa = false,
    tipoNota: "NFe" | "NFCe" | "NFSe" = "NFe"
  ): Promise<{ ok: boolean; data?: FocusNFNota; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("consultarNota", {
        ref,
        completa,
        tipoNota,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao consultar nota" };
    }
  }

  async listarNotas(orderCode?: string): Promise<FocusNFNota[]> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) return [];

      let query = supabase
        .from("focusnf_notas")
        .select("*")
        .eq("empresa_id", empresa_id)
        .order("created_at", { ascending: false });

      if (orderCode) {
        query = query.eq("order_code", orderCode);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as FocusNFNota[];
    } catch (e) {
      console.error("Erro ao listar notas:", e);
      return [];
    }
  }

  async limparNotasComErroDoPedido(
    orderCode: string
  ): Promise<{ ok: boolean; count?: number; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const statusComErro = [
        "erro_emissao",
        "erro_autorizacao",
        "erro_validacao",
        "erro_validacao_schema",
        "denegado",
      ];

      const { data, error } = await supabase
        .from("focusnf_notas")
        .update({
          order_id: null,
          order_code: null,
          updated_at: new Date().toISOString(),
        })
        .eq("empresa_id", empresa_id)
        .eq("order_code", orderCode)
        .in("status", statusComErro)
        .select("id");

      if (error) throw error;

      return { ok: true, count: data?.length || 0 };
    } catch (e: any) {
      console.error("Erro ao limpar notas com erro do pedido:", e);
      return { ok: false, error: e.message || "Erro ao limpar notas com erro" };
    }
  }

  async cancelarNota(
    ref: string,
    justificativa: string,
    tipoNota: "NFe" | "NFCe" | "NFSe" = "NFe"
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("cancelarNota", {
        ref,
        justificativa,
        tipoNota,
        empresaId: empresa_id,
      });
      return { ok: result.success, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao cancelar nota" };
    }
  }

  async emitirCartaCorrecao(
    ref: string,
    correcao: string
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("emitirCartaCorrecao", {
        ref,
        correcao,
        empresaId: empresa_id,
      });
      return { ok: result.success, error: result.error };
    } catch (e: any) {
      return {
        ok: false,
        error: e.message || "Erro ao emitir carta de correção",
      };
    }
  }

  async cadastrarEmitente(): Promise<{
    ok: boolean;
    data?: { id?: string; cnpj: string; status?: string };
    error?: string;
  }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        return { ok: false, error: "Empresa não encontrada" };
      }

      const result = await this.makeRequest("cadastrarEmitente", {
        empresaId: empresa_id,
      });

      if (result.success && result.data?.id) {
        await supabase
          .from("focusnf_config")
          .update({
            focus_empresa_id: result.data.id,
            updated_at: new Date().toISOString(),
          })
          .eq("empresa_id", empresa_id);
      }

      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao cadastrar emitente" };
    }
  }

  async enviarEmail(
    ref: string,
    emails: string[]
  ): Promise<{ ok: boolean; data?: { emails?: string[] }; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("enviarEmail", {
        ref,
        emails,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao enviar email" };
    }
  }

  async registrarAtorInteressado(params: {
    ref: string;
    cpf?: string;
    cnpj?: string;
    permite_autorizacao_terceiros?: boolean;
  }): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("registrarAtorInteressado", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return {
        ok: false,
        error: e.message || "Erro ao registrar ator interessado",
      };
    }
  }

  async registrarEvento(
    params: { ref: string; tipo_evento: string } & Record<string, unknown>
  ): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("registrarEvento", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao registrar evento" };
    }
  }

  async registrarInsucessoEntrega(params: {
    ref: string;
    data_tentativa_entrega: string;
    numero_tentativas?: string;
    motivo_insucesso: string;
    justificativa_insucesso?: string;
    latitude_entrega?: string;
    longitude_entrega?: string;
    hash_tentativa_entrega: string;
    data_hash_tentativa?: string;
  }): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("registrarInsucessoEntrega", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return {
        ok: false,
        error: e.message || "Erro ao registrar insucesso na entrega",
      };
    }
  }

  async cancelarInsucessoEntrega(
    ref: string
  ): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("cancelarInsucessoEntrega", {
        ref,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return {
        ok: false,
        error: e.message || "Erro ao cancelar insucesso na entrega",
      };
    }
  }

  async inutilizarNumeracao(params: {
    cnpj: string;
    serie: string;
    numero_inicial: string;
    numero_final: string;
    justificativa: string;
  }): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("inutilizarNumeracao", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao inutilizar numeração" };
    }
  }

  async consultarInutilizacoes(params: {
    cnpj?: string;
    cpf?: string;
    data_recebimento_inicial?: string;
    data_recebimento_final?: string;
    numero_inicial?: string;
    numero_final?: string;
  }): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("consultarInutilizacoes", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao consultar inutilizações" };
    }
  }

  async registrarEconf(params: {
    ref: string;
    detalhes_pagamento: Record<string, unknown>[];
  }): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("registrarEconf", {
        ...params,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao registrar ECONF" };
    }
  }

  async consultarEconf(
    ref: string,
    numero_protocolo: string
  ): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("consultarEconf", {
        ref,
        numero_protocolo,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao consultar ECONF" };
    }
  }

  async cancelarEconf(
    ref: string,
    numero_protocolo: string
  ): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const empresa_id = await getCurrentEmpresaId();
      const result = await this.makeRequest("cancelarEconf", {
        ref,
        numero_protocolo,
        empresaId: empresa_id,
      });
      return { ok: result.success, data: result.data, error: result.error };
    } catch (e: any) {
      return { ok: false, error: e.message || "Erro ao cancelar ECONF" };
    }
  }
}

export const focusNFService = new FocusNFService();
